// Iter 40 — Multi-day picker for constraint editors.
// Replaces the legacy single-select day_of_week dropdown anywhere a
// constraint repeats across the school week. Modes:
//
//   • "multi"   → checkbox-style 5-day selector (Mon · Tue · Wed · Thu · Fri).
//                 Returns int[] of weekday indices (Mon=1 … Fri=5).
//                 An optional "Every weekday" toggle promotes the full
//                 set to a single -1 sentinel (matches existing data
//                 model semantics: day_of_week=-1 means "any day").
//
//   • "single"  → identical visual treatment but enforces single
//                 selection (drop-in replacement when callers don't
//                 want multi). Also returns int[] for shape consistency.
//
// The component is purely presentational; persistence is the caller's
// job. Constraint save handlers use `expandDays(rows, draft)` below to
// fan a multi-day draft into N single-day rows so the underlying data
// model stays unchanged.
import { useMemo } from "react";

const buildOptions = (dayBase) => [
  { value: dayBase + 0, label: "Mon" },
  { value: dayBase + 1, label: "Tue" },
  { value: dayBase + 2, label: "Wed" },
  { value: dayBase + 3, label: "Thu" },
  { value: dayBase + 4, label: "Fri" },
];

export function DayOfWeekMultiPicker({
  value,
  onChange,
  mode = "multi",
  disabled = false,
  testid = "day-multi",
  showEveryDay = true,
  className = "",
  // Iter 41 — `dayBase` lets callers pin the numeric origin. Default 1
  // preserves the original flex_day call shape; backend constraint
  // editors (grade/provider/school) pass 0 to match `0=Mon..4=Fri`.
  dayBase = 1,
}) {
  const WEEKDAY_OPTIONS = useMemo(() => buildOptions(dayBase), [dayBase]);
  // Normalize incoming value into an int[] regardless of caller shape.
  // -1 (legacy "every day") stays as a single-item [-1] for the
  // "Every weekday" toggle.
  const days = useMemo(() => {
    if (Array.isArray(value)) return value.map(Number).filter((n) => !Number.isNaN(n));
    if (value === null || value === undefined || value === "") return [];
    const n = Number(value);
    if (Number.isNaN(n)) return [];
    return [n];
  }, [value]);

  const isEveryDay = days.length === 1 && days[0] === -1;
  const isAllWeekdays = days.length === 5 && WEEKDAY_OPTIONS.every((d) => days.includes(d.value));

  const toggleDay = (d) => {
    if (disabled) return;
    if (mode === "single") {
      onChange([d]);
      return;
    }
    if (isEveryDay) {
      onChange([d]);
      return;
    }
    if (days.includes(d)) {
      const next = days.filter((x) => x !== d);
      onChange(next.length ? next : [d]); // never empty
    } else {
      onChange([...days, d].sort((a, b) => a - b));
    }
  };

  const toggleEveryDay = () => {
    if (disabled || mode !== "multi") return;
    onChange(isEveryDay ? [WEEKDAY_OPTIONS[0].value] : [-1]);
  };

  const toggleAllWeekdays = () => {
    if (disabled || mode !== "multi") return;
    onChange(isAllWeekdays ? [WEEKDAY_OPTIONS[0].value] : WEEKDAY_OPTIONS.map((d) => d.value));
  };

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid={testid}>
      {WEEKDAY_OPTIONS.map((opt) => {
        const selected = isEveryDay || days.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`${testid}-day-${opt.value}`}
            disabled={disabled}
            onClick={() => toggleDay(opt.value)}
            aria-pressed={selected}
            className={`relative px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors
              ${selected
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white text-[#4B5563] border-[#E5E3DB] hover:bg-[#F3F1EC] hover:border-[#C9C5BC]"}
              ${disabled ? "opacity-50 cursor-not-allowed" : ""}
              ${isEveryDay ? "opacity-70" : ""}
            `}
          >
            {opt.label}
          </button>
        );
      })}
      {mode === "multi" && (
        <>
          <span className="w-px h-5 bg-[#E5E3DB] mx-1" aria-hidden />
          <button
            type="button"
            data-testid={`${testid}-all-weekdays`}
            disabled={disabled}
            onClick={toggleAllWeekdays}
            className={`px-2.5 py-1 rounded-md text-[10px] font-medium border transition-colors
              ${isAllWeekdays
                ? "bg-[#3F4756] text-white border-[#3F4756]"
                : "bg-white text-[#4B5563] border-[#E5E3DB] hover:bg-[#F3F1EC]"}
              ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            `}
            title="Mon–Fri"
          >
            All
          </button>
          {showEveryDay && (
            <button
              type="button"
              data-testid={`${testid}-every-day`}
              disabled={disabled}
              onClick={toggleEveryDay}
              className={`px-2.5 py-1 rounded-md text-[10px] font-medium border transition-colors
                ${isEveryDay
                  ? "bg-[#3F4756] text-white border-[#3F4756]"
                  : "bg-white text-[#4B5563] border-[#E5E3DB] hover:bg-[#F3F1EC]"}
                ${disabled ? "opacity-50 cursor-not-allowed" : ""}
              `}
              title="Applies to any day (uses -1 sentinel)"
            >
              Every day
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Helper for save handlers — converts a draft with `days: int[]` into
// N row-shaped objects, one per selected day. -1 (every day) stays as
// a single row with day_of_week=-1.
//
// Usage:
//   const rows = expandDays(draft, "day_of_week");
//   await Promise.all(rows.map(api.createConstraint));
export function expandDays(draft, fieldName = "day_of_week") {
  const days = Array.isArray(draft.days) && draft.days.length ? draft.days : [draft[fieldName]];
  const cleaned = days
    .map((d) => (typeof d === "string" ? parseInt(d, 10) : d))
    .filter((d) => d !== null && d !== undefined && !Number.isNaN(d));
  if (cleaned.length === 0) return [{ ...draft, [fieldName]: draft[fieldName] }];
  // -1 collapses to single row regardless of accompanying days
  if (cleaned.includes(-1)) return [{ ...draft, [fieldName]: -1 }];
  return cleaned.map((d) => ({ ...draft, [fieldName]: d }));
}
